import { format } from 'date-fns';

import { SmsMessage } from '@ndvr/customer-api';

import { formatPhoneNumber } from 'utils/string-utils';

/** Palette used to color-code senders by a stable hash of their phone number. */
const SENDER_COLORS = [
    '#2C6E72',
    '#8A6233',
    '#6E5BA6',
    '#2F7D52',
    '#B5562B',
    '#3A6FA0',
    '#9A4B6B',
    '#4A7A3A',
    '#7A6A2E',
    '#557089',
    '#905A8A',
    '#3C7E80',
];

/** Formats a bare phone number for display, falling back to the raw value when it isn't 10 digits. */
export const formatPhone = (phoneNumber?: string): string =>
    (phoneNumber && formatPhoneNumber(phoneNumber)) || phoneNumber || '';

/** A party's display name, falling back to the formatted number, then a generic label. */
export const partyName = (name?: string, phoneNumber?: string): string =>
    name?.trim() || formatPhone(phoneNumber) || 'Unknown';

/** Deterministic avatar/dot color for a sender, keyed by their phone number (or name as a fallback). */
export const senderColor = (key?: string): string => {
    const source = key || '';
    let hash = 0;
    for (let i = 0; i < source.length; i += 1) {
        hash = (hash * 31 + source.charCodeAt(i)) | 0;
    }
    return SENDER_COLORS[Math.abs(hash) % SENDER_COLORS.length];
};

/** Two-letter initials from a display name. */
export const initials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

export const isOutbound = (message: SmsMessage): boolean => message.direction === 'outbound';

/** The non-advisor number on a message (the side opposite the advisor's line). */
export const counterpartyNumber = (message: SmsMessage): string | undefined =>
    isOutbound(message) ? message.toNumber : message.fromNumber;

const sentTime = (message: SmsMessage): number => (message.sentAt ? new Date(message.sentAt).getTime() : 0);

/** Collapses a message body to a single-line preview. */
export const previewText = (message?: SmsMessage): string => (message?.message || '').replace(/\s+/g, ' ').trim();

/** The distinct counterparties seen across a set of messages, newest contact first. */
export const counterparties = (messages: SmsMessage[]): SmsMessage[] => {
    const seen = new Map<string, SmsMessage>();
    [...messages]
        .sort((a, b) => sentTime(b) - sentTime(a))
        .forEach((message) => {
            const number = counterpartyNumber(message);
            if (number && !seen.has(number)) seen.set(number, message);
        });
    return Array.from(seen.values());
};

export interface DayGroup {
    label: string;
    items: SmsMessage[];
}

/** Groups the household's messages by calendar day (oldest first), for the conversation thread. */
export const groupByDay = (messages: SmsMessage[]): DayGroup[] => {
    const oldestFirst = [...messages].sort((a, b) => sentTime(a) - sentTime(b));
    const groups: DayGroup[] = [];
    oldestFirst.forEach((message) => {
        const label = message.sentAt ? format(new Date(message.sentAt), 'MMMM d, yyyy') : 'Unknown date';
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.items.push(message);
        else groups.push({ label, items: [message] });
    });
    return groups;
};

/** Time-of-day label for a message bubble, e.g. "1:53 PM". */
export const messageTime = (message: SmsMessage): string =>
    message.sentAt ? format(new Date(message.sentAt), 'h:mm a') : '';
